import { auth, db } from '../firebaseConfig';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import { collection, addDoc, serverTimestamp, query, orderBy, onSnapshot, getDocs } from 'firebase/firestore';
import { format } from 'date-fns';
import { FaPaperPlane } from 'react-icons/fa6';
import { Notification } from '../components/HeroNotification';
import { MessageCloud } from '../components/MessageCloud';
import { MessageCloudModal } from '../components/MessageCloudModal';
import { Message } from '../types/types';

// Definimos el tipo para el estado de la notificación
type NotificationState = {
  message: string;
  type: 'success' | 'error';
} | null;

interface HeroSectionProps {
  onTitleChange?: (title: string) => void; // Callback to send title to navbar
}

export const HeroSection = ({ onTitleChange }: HeroSectionProps) => {
  const user = auth.currentUser;
  const [title, setTitle] = useState(`¡Hola, ${user?.displayName || 'linda'}!`);
  const [notification, setNotification] = useState<NotificationState>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Check for special date and update title
  useEffect(() => {
    const getGreeting = () => {
      const hour = new Date().getHours();
      if (hour < 12) return "¡Buenos días, hermosa!";
      if (hour < 19) return "¡Buenas tardes, linda!";
      return "¡Buenas noches, mi vida!";
    };

    const checkSpecialDate = async () => {
      const today = format(new Date(), 'yyyy-MM-dd');
      const q = query(collection(db, "specialDates"));
      const querySnapshot = await getDocs(q);
      let specialTitle = null;
      querySnapshot.forEach((doc) => {
        if (doc.data().date === today) {
          specialTitle = doc.data().title;
        }
      });

      const finalTitle = specialTitle || getGreeting();
      setTitle(finalTitle);

      // Always send title to navbar, even if no callback (for debugging)
      if (onTitleChange) {
        onTitleChange(finalTitle);
      }
    };

    checkSpecialDate();

    // Also update navbar when component mounts/updates
    const interval = setInterval(() => {
      checkSpecialDate();
    }, 60000); // Re-check every minute to update greeting based on time

    return () => clearInterval(interval);
  }, [onTitleChange]);

  // Load messages from database with real-time updates
  useEffect(() => {
    const q = query(
      collection(db, 'messages'),
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const msgs: Message[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Only include visible messages (client-side filtering)
        if (data.isVisible !== false) {
          msgs.push({
            id: doc.id,
            text: data.text,
            timestamp: data.timestamp ? data.timestamp.toDate() : new Date(),
            from: data.from || user?.uid || 'unknown',
            color: data.color,
            isVisible: data.isVisible !== false,
          });
        }
      });
      // Reverse to show oldest first (bottom = newest, like chat apps)
      setMessages(msgs.reverse());
    }, (error) => {
      console.error("Error loading messages:", error);
    });

    return () => unsubscribe();
  }, [user]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (messageText: string) => {
    if (messageText.trim() === '') return;
    try {
      await addDoc(collection(db, 'messages'), {
        text: messageText,
        timestamp: serverTimestamp(),
        from: user?.uid || 'unknown',
        isVisible: true,
      });

      setNotification({ message: '¡Mensaje enviado con éxito!', type: 'success' });
    } catch (error) {
      console.error("Error al guardar el mensaje: ", error);
      setNotification({ message: 'No se pudo enviar el mensaje.', type: 'error' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex flex-col items-center justify-center p-4 pt-20 pb-4 overflow-hidden">

      {/* Notification */}
      <AnimatePresence>
        {notification && (
          <Notification
            message={notification.message}
            type={notification.type}
            onClose={() => setNotification(null)}
          />
        )}
      </AnimatePresence>

      {/* Message Board - Full Screen Chat */}
      <div className="relative w-full max-w-4xl flex-1 flex flex-col items-center justify-start">
        {/* Message Clouds Container - Hidden Scrollbar */}
        <div className="w-full flex flex-col items-center gap-2 overflow-y-auto h-full pr-4 pb-4 px-4 scrollbar-hide">
          {messages.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <p className="text-text-light text-lg font-script">
                Aún no hay mensajes en el cielo...
              </p>
              <p className="text-text-light text-sm mt-2">
                ¡Envía el primero! ✈️
              </p>
            </motion.div>
          ) : (
            <>
              {messages.map((msg, index) => (
                <MessageCloud key={msg.id} message={msg} index={index} />
              ))}
              {/* Invisible div to scroll to */}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Airplane Button - Fixed at Bottom Right */}
        <motion.button
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          whileHover={{ scale: 1.1, rotate: -5 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsModalOpen(true)}
          className="fixed bottom-24 right-8 w-16 h-16 bg-secondary text-primary rounded-full shadow-2xl flex items-center justify-center z-20 hover:bg-primary hover:text-white transition-all duration-300 border-4 border-white"
          aria-label="Enviar mensaje"
        >
          <FaPaperPlane className="text-2xl" />
        </motion.button>
      </div>

      {/* Message Modal */}
      <MessageCloudModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSend={handleSendMessage}
      />
    </section>
  );
};
